import { useEffect, useRef, useCallback } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet.markercluster';

const getQualityBand = (score) => {
  if (!score || score < 30) return 'Low';
  if (score < 60) return 'Medium';
  if (score < 80) return 'Good';
  return 'Excellent';
};

const QUALITY_COLORS = {
  'Low': '#ef4444', 'Medium': '#f59e0b', 'Good': '#3b82f6', 'Excellent': '#10b981'
};

const DOC_TYPE_COLORS = {
  'News Article': '#ef4444', 'Blog Post': '#F59E0B', 'Academic Paper': '#3b82f6',
  'Wiki': '#10b981', 'Forum': '#FDE047', 'Government': '#06b6d4',
  'Video': '#FDE68A', 'PhD Informative': '#FDE047', 'PhD Dissertation': '#FCD34D',
  'Research Paper': '#6d28d9', 'Technical Drawing': '#a855f7', 'Blueprint': '#9333ea',
  'Patent': '#c026d3', 'Instructional Manual': '#d946ef', 'PDF Document': '#dc2626',
  'MS Word Document': '#2563eb', 'Informative': '#14b8a6',
  'Personal Report (Organic)': '#10b981', 'Personal Report (Collected)': '#14b8a6',
  'Unknown': '#6b7280'
};

const getColorForKey = (key, mode, categoryColorMap) => {
  if (mode === 'quality') return QUALITY_COLORS[key] || '#6b7280';
  if (mode === 'doctype') return DOC_TYPE_COLORS[key] || '#6b7280';
  return categoryColorMap[key] || '#6b7280';
};

const getDistribution = (markers, mode, categoryColorMap) => {
  const dist = {};
  markers.forEach(m => {
    const d = m.options.resultData;
    if (!d) return;
    let key;
    if (mode === 'quality') key = getQualityBand(d.content_quality_score);
    else if (mode === 'doctype') key = d.article_type || 'Unknown';
    else key = d.categories?.[0] || 'Uncategorized';
    dist[key] = (dist[key] || 0) + 1;
  });
  return dist;
};

// ─── SVG pie-arc helper ───
const arcPath = (cx, cy, r, startA, endA) => {
  const x1 = cx + r * Math.cos(startA), y1 = cy + r * Math.sin(startA);
  const x2 = cx + r * Math.cos(endA), y2 = cy + r * Math.sin(endA);
  const large = endA - startA > Math.PI ? 1 : 0;
  return `M${cx},${cy} L${x1},${y1} A${r},${r} 0 ${large} 1 ${x2},${y2} Z`;
};

const piePaths = (entries, cx, cy, r, mode, catMap) => {
  const total = entries.reduce((s, [, v]) => s + v, 0);
  let a = -Math.PI / 2;
  return entries.map(([key, value]) => {
    const sweep = (value / total) * 2 * Math.PI;
    const end = a + sweep;
    const color = getColorForKey(key, mode, catMap);
    const d = arcPath(cx, cy, r, a, end);
    a = end;
    return `<path d="${d}" fill="${color}" stroke="#475569" stroke-width="0.6"/>`;
  }).join('');
};

// ─── MULTI-DATASET branched cluster icon ───
const createMultiDatasetSvg = (childMarkers, activeModes, categoryColorMap) => {
  const count = childMarkers.length;
  const modes = activeModes.filter(m => m); // e.g. ['category','doctype','quality']
  const modeCount = modes.length;

  // Base size scales with marker count
  const baseSize = count < 8 ? 40 : count < 25 ? 48 : count < 60 ? 56 : count < 150 ? 64 : 72;
  // Bigger canvas for multi-dataset
  const padding = modeCount > 1 ? 14 : 0;
  const size = baseSize + padding * 2;
  const cx = size / 2, cy = size / 2;
  const mainR = baseSize / 2 - 3;

  if (modeCount === 0) {
    return { svg: '', size };
  }

  // Single mode — simple pie
  if (modeCount === 1) {
    const dist = getDistribution(childMarkers, modes[0], categoryColorMap);
    const entries = Object.entries(dist).filter(([, v]) => v > 0);
    if (entries.length === 0) return { svg: '', size };

    let paths;
    if (entries.length === 1) {
      const c = getColorForKey(entries[0][0], modes[0], categoryColorMap);
      paths = `<circle cx="${cx}" cy="${cy}" r="${mainR}" fill="${c}" stroke="white" stroke-width="2"/>`;
    } else {
      paths = `<circle cx="${cx}" cy="${cy}" r="${mainR}" fill="transparent" stroke="white" stroke-width="2"/>` + piePaths(entries, cx, cy, mainR, modes[0], categoryColorMap);
    }
    const innerR = Math.max(mainR * 0.4, 9);
    const fs = baseSize < 48 ? 10 : baseSize < 56 ? 12 : 14;
    const svg = `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg">
      ${paths}
      <circle cx="${cx}" cy="${cy}" r="${innerR}" fill="rgba(253, 251, 247,0.92)" stroke="#334155" stroke-width="1.2"/>
      <text x="${cx}" y="${cy}" text-anchor="middle" dy="0.35em" fill="white" font-size="${fs}" font-weight="700" font-family="system-ui">${count}</text>
    </svg>`;
    return { svg, size };
  }

  // Multi-mode — branched layout (2 or 3 datasets)
  // Central count node + satellite pie rings
  const centerR = Math.max(mainR * 0.42, 12);
  const satR = Math.max(mainR * 0.32, 10);
  const orbitR = mainR * 0.7;

  // Position satellites evenly around center
  const angleStep = (2 * Math.PI) / modeCount;
  const startA = -Math.PI / 2;

  let svgContent = '';
  // Connecting lines (branches)
  modes.forEach((mode, i) => {
    const a = startA + i * angleStep;
    const sx = cx + orbitR * Math.cos(a);
    const sy = cy + orbitR * Math.sin(a);
    svgContent += `<line x1="${cx}" y1="${cy}" x2="${sx}" y2="${sy}" stroke="rgba(255,255,255,0.25)" stroke-width="1.5"/>`;
  });

  // Satellite pies
  const modeLabels = { category: 'C', doctype: 'D', quality: 'Q' };
  const modeRingColors = { category: '#047857', doctype: '#f59e0b', quality: '#10b981' };
  modes.forEach((mode, i) => {
    const a = startA + i * angleStep;
    const sx = cx + orbitR * Math.cos(a);
    const sy = cy + orbitR * Math.sin(a);
    const dist = getDistribution(childMarkers, mode, categoryColorMap);
    const entries = Object.entries(dist).filter(([, v]) => v > 0);
    if (entries.length === 1) {
      const c = getColorForKey(entries[0][0], mode, categoryColorMap);
      svgContent += `<circle cx="${sx}" cy="${sy}" r="${satR}" fill="${c}" stroke="${modeRingColors[mode]}" stroke-width="1.8"/>`;
    } else if (entries.length > 1) {
      svgContent += `<circle cx="${sx}" cy="${sy}" r="${satR}" fill="transparent" stroke="${modeRingColors[mode]}" stroke-width="1.8"/>`;
      svgContent += piePaths(entries, sx, sy, satR, mode, categoryColorMap);
    }
    svgContent += `<text x="${sx}" y="${sy}" text-anchor="middle" dy="0.35em" fill="white" font-size="8" font-weight="700" font-family="system-ui">${modeLabels[mode]}</text>`;
  });

  // Central count circle
  svgContent += `<circle cx="${cx}" cy="${cy}" r="${centerR}" fill="rgba(253, 251, 247,0.95)" stroke="#1e293b" stroke-width="1.5"/>`;
  const fs = baseSize < 48 ? 10 : baseSize < 56 ? 12 : 14;
  svgContent += `<text x="${cx}" y="${cy}" text-anchor="middle" dy="0.35em" fill="white" font-size="${fs}" font-weight="700" font-family="system-ui">${count}</text>`;

  const svg = `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg">${svgContent}</svg>`;
  return { svg, size };
};

// Single marker icon colored by primary active mode
const createSingleMarkerIcon = (result, activeModes, categoryColorMap) => {
  const primary = activeModes[0] || 'category';
  let color;
  if (primary === 'quality') color = QUALITY_COLORS[getQualityBand(result.content_quality_score)] || '#6b7280';
  else if (primary === 'doctype') color = DOC_TYPE_COLORS[result.article_type] || '#6b7280';
  else { const c = result.categories?.[0]; color = (c && categoryColorMap[c]) || DOC_TYPE_COLORS[result.article_type] || '#6b7280'; }
  // Multi-mode: stack tiny indicator dots
  const indicators = activeModes.length > 1 ? activeModes.slice(1).map((mode, i) => {
    let ic;
    if (mode === 'quality') ic = QUALITY_COLORS[getQualityBand(result.content_quality_score)] || '#6b7280';
    else if (mode === 'doctype') ic = DOC_TYPE_COLORS[result.article_type] || '#6b7280';
    else { const c = result.categories?.[0]; ic = (c && categoryColorMap[c]) || '#6b7280'; }
    return `<div style="position:absolute;top:${-3 - i * 6}px;right:-3px;width:8px;height:8px;background:${ic};border:1px solid white;border-radius:50%;"></div>`;
  }).join('') : '';
  return L.divIcon({
    className: '',
    html: `<div style="position:relative;width:20px;height:20px;background:${color};border:2.5px solid rgba(255,255,255,0.85);border-radius:50%;box-shadow:0 0 8px ${color},0 2px 5px rgba(0,0,0,0.35);cursor:pointer;">${indicators}</div>`,
    iconSize: [20, 20], iconAnchor: [10, 10], popupAnchor: [0, -10]
  });
};

// Build popup HTML for cluster hover
const buildClusterPopupHtml = (childMarkers, activeModes, categoryColorMap) => {
  const results = childMarkers.map(m => m.options.resultData).filter(Boolean);
  const maxShow = 8;
  const shown = results.slice(0, maxShow);

  let rows = shown.map(r => {
    const cat = r.categories?.[0] || '';
    const dt = r.article_type || 'Unknown';
    const q = getQualityBand(r.content_quality_score);
    const qColor = QUALITY_COLORS[q];
    const dtColor = DOC_TYPE_COLORS[dt] || '#6b7280';
    const catColor = categoryColorMap[cat] || '#6b7280';
    const title = (r.title || r.url || 'Untitled').substring(0, 55);
    const snippet = (r.snippet || '').substring(0, 80);
    return `<div style="padding:6px 0;border-bottom:1px solid rgba(255,255,255,0.06);">
      <a href="${r.url || '#'}" target="_blank" rel="noopener" style="color:#047857;font-size:0.78rem;font-weight:600;text-decoration:none;display:block;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;" title="${(r.title || '').replace(/"/g, '&quot;')}">${title}</a>
      ${snippet ? `<div style="color:#71717a;font-size:0.68rem;margin-top:2px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${snippet}</div>` : ''}
      <div style="display:flex;gap:4px;margin-top:3px;flex-wrap:wrap;">
        ${cat ? `<span style="padding:1px 5px;border-radius:4px;font-size:0.6rem;background:${catColor}22;color:${catColor};border:1px solid ${catColor}44;">${cat.substring(0, 18)}</span>` : ''}
        <span style="padding:1px 5px;border-radius:4px;font-size:0.6rem;background:${dtColor}22;color:${dtColor};border:1px solid ${dtColor}44;">${dt}</span>
        <span style="padding:1px 5px;border-radius:4px;font-size:0.6rem;background:${qColor}22;color:${qColor};border:1px solid ${qColor}44;">${q}</span>
      </div>
    </div>`;
  }).join('');

  if (results.length > maxShow) {
    rows += `<div style="color:#71717a;font-size:0.7rem;padding:6px 0;text-align:center;">+ ${results.length - maxShow} more URLs</div>`;
  }

  return `<div style="max-height:320px;overflow-y:auto;padding:8px 10px;">
    <div style="color:#e2e8f0;font-size:0.82rem;font-weight:700;margin-bottom:6px;display:flex;align-items:center;gap:6px;">
      <span style="display:inline-block;width:10px;height:10px;background:linear-gradient(135deg,#FCD34D,#FDE68A);border-radius:50%;"></span>
      ${results.length} URLs in this area
    </div>
    ${rows}
  </div>`;
};

// ─── Main component ───
const PieChartMarkers = ({
  results, activeModes, categoryColorMap,
  onMarkerClick, onMarkerHover, onMarkerLeave,
  granularMode = false
}) => {
  const map = useMap();
  const clusterRef = useRef(null);

  const buildCluster = useCallback(() => {
    if (clusterRef.current) { map.removeLayer(clusterRef.current); clusterRef.current = null; }
    if (!results || results.length === 0) return;

    const group = L.markerClusterGroup({
      chunkedLoading: true,
      maxClusterRadius: granularMode ? 20 : 55,
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
      animate: true,
      iconCreateFunction: (cluster) => {
        const children = cluster.getAllChildMarkers();
        const count = children.length;

        if (granularMode) {
          // Granular: smaller, simpler clusters
          const sz = count < 5 ? 26 : count < 15 ? 32 : 38;
          const primary = activeModes[0] || 'category';
          const dist = getDistribution(children, primary, categoryColorMap);
          const entries = Object.entries(dist).filter(([, v]) => v > 0);
          const topColor = entries.length > 0 ? getColorForKey(entries[0][0], primary, categoryColorMap) : '#6b7280';
          return L.divIcon({
            html: `<div style="width:${sz}px;height:${sz}px;background:${topColor};border:2px solid rgba(255,255,255,0.8);border-radius:50%;display:flex;align-items:center;justify-content:center;color:white;font-size:${sz < 32 ? 9 : 11}px;font-weight:700;font-family:system-ui;box-shadow:0 2px 8px rgba(0,0,0,0.4);cursor:pointer;">${count}</div>`,
            className: '', iconSize: L.point(sz, sz), iconAnchor: L.point(sz / 2, sz / 2)
          });
        }

        // Standard: multi-dataset branched pie
        const { svg, size } = createMultiDatasetSvg(children, activeModes, categoryColorMap);
        return L.divIcon({
          html: `<div style="filter:drop-shadow(0 3px 8px rgba(0,0,0,0.5));cursor:pointer;">${svg}</div>`,
          className: '', iconSize: L.point(size, size), iconAnchor: L.point(size / 2, size / 2)
        });
      }
    });

    // Cluster hover popup
    group.on('clustermouseover', function (e) {
      const cluster = e.layer;
      const children = cluster.getAllChildMarkers();
      const html = buildClusterPopupHtml(children, activeModes, categoryColorMap);
      L.popup({ closeButton: false, className: 'cluster-popup', offset: L.point(0, -20), maxWidth: 340 })
        .setLatLng(cluster.getLatLng())
        .setContent(html)
        .openOn(map);
    });
    group.on('clustermouseout', function () {
      map.closePopup();
    });

    // Add markers
    results.forEach((result) => {
      if (!result.latitude || !result.longitude) return;
      const marker = L.marker([result.latitude, result.longitude], {
        icon: createSingleMarkerIcon(result, activeModes, categoryColorMap),
        resultData: result
      });
      marker.on('click', () => onMarkerClick(result));
      marker.on('mouseover', (e) => onMarkerHover(result, e));
      marker.on('mouseout', () => onMarkerLeave());
      group.addLayer(marker);
    });

    group.addTo(map);
    clusterRef.current = group;
  }, [results, activeModes, categoryColorMap, map, onMarkerClick, onMarkerHover, onMarkerLeave, granularMode]);

  useEffect(() => {
    buildCluster();
    return () => { if (clusterRef.current) { map.removeLayer(clusterRef.current); clusterRef.current = null; } };
  }, [buildCluster]);

  return null;
};

export { PieChartMarkers, getQualityBand, QUALITY_COLORS, DOC_TYPE_COLORS };
export default PieChartMarkers;
